import {
  Condition,
  FeeCategory,
  FinanceType,
  IncentivesProvider,
  Makes,
  OfferCategoryType,
  PrintingType,
  TabletForms,
} from '@upstart/harmony/lib/constants';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { type FormData } from '../types';

export const InventorySortValues = {
  PRICE_ASC: 'priceAsc',
  PRICE_DESC: 'priceDesc',
  MILEAGE_ASC: 'mileageAsc',
  MILEAGE_DESC: 'mileageDesc',
  YEAR_ASC: 'yearAsc',
  YEAR_DESC: 'yearDesc',
};

export const InventoryImageSources = {
  CHROME_DATA: 'Chrome Data',
  DEALER: 'Dealer',
  SDIS: 'Shift Digital Toyota/Lexus Inventory Images',
};

type InventoryImageSouceConfigValues = {
  defaultImageSource: string;
  optionValues: string[];
};

const getInventoryImageSchemaConfigValues = (
  dealer: Dealer,
): InventoryImageSouceConfigValues => {
  // Set the image source option default to current selected option. Have to process here, as default is static.
  const isShiftImageEnabled =
    !!dealer.featureConfig?.inventoryImages
      ?.shiftDigitalInventorySolutionIsPrimaryImageSource;
  const isChromeImageEnabled =
    !!dealer.featureConfig?.inventoryImages?.chromeDataIsPrimaryImageSource;
  let defaultImageSource = InventoryImageSources.DEALER;
  if (isShiftImageEnabled) {
    defaultImageSource = InventoryImageSources.SDIS;
  } else if (isChromeImageEnabled) {
    defaultImageSource = InventoryImageSources.CHROME_DATA;
  }

  const isToyotaOrLexusDealer = dealer?.brand?.some((brand) => {
    const make = brand.toLowerCase();
    return (
      make === Makes.Toyota.toLowerCase() || make === Makes.Lexus.toLowerCase()
    );
  });

  const optionValues = isToyotaOrLexusDealer
    ? Object.values(InventoryImageSources)
    : Object.values(InventoryImageSources).filter(
        (source) => source !== InventoryImageSources.SDIS,
      );

  return { defaultImageSource, optionValues };
};

const incentivesProviders = {
  AIS: IncentivesProvider.AIS,
};

export const getStep8Schema = (formData: FormData) => {
  const leaseFees = formData?.fees?.[Condition.NEW]?.lease;
  const hasUpstartEnabled = !!formData?.lending?.upstart?.enabled;

  const isAudiDealer = formData?.brand
    ?.map((brand) => brand.toLowerCase())
    .includes(Makes.Audi);
  const uniteDealerCode = formData.featureConfig?.unite?.dealerCode;

  const inventoryImageSchemaValues =
    getInventoryImageSchemaConfigValues(formData);

  return {
    title: 'Configuration',
    type: 'object',
    properties: {
      printingType: {
        type: 'string',
        title: 'Printer',
        enum: Object.values(PrintingType),
      },
      supportEmail: {
        type: ['string', 'null'],
        title: 'Support Email',
        default: '',
      },
      defaultApr: {
        type: 'number',
        description:
          'Used for default monthly payments in places like car cards',
        title: 'Default Apr',
      },
      testDriveSpeed: {
        type: 'integer',
        title: 'Test-Drive Speed (mph)',
        minimum: 0,
        maximum: 100,
      },
      testDriveDuration: {
        type: 'integer',
        title: 'Test-Drive Duration (seconds)',
        minimum: 0,
        maximum: 6000,
      },
      tabletFormsConfig: {
        type: 'object',
        title: 'Instore Configurations',
        properties: {
          [TabletForms.CUSTOMER_INFO]: {
            type: 'object',
            title: 'Customer Info',
            properties: {
              zipCode: {
                type: 'object',
                title: '',
                properties: {
                  required: {
                    type: 'boolean',
                    title: 'Require Zip Code',
                  },
                },
              },
              preferredContact: {
                type: 'object',
                title: '',
                properties: {
                  required: {
                    type: 'boolean',
                    title: 'Display Preferred Contact Method',
                  },
                },
              },
              trackingCode: {
                type: 'object',
                title: '',
                properties: {
                  required: {
                    type: 'boolean',
                    title: 'Require Tracking Code',
                  },
                },
              },
              allowFormSkip: {
                type: 'boolean',
                title: 'Allow user to skip the form with validation errors',
              },
              disableCustomerSignature: {
                type: 'boolean',
                title: 'Disable Customer Signature Option',
              },
              showInstoreOnlineLink: {
                type: 'boolean',
                title: `Controls visibility of the 'Send Deal to Customer' link`,
              },
              useAddCarWithoutVIN: {
                type: 'boolean',
                title: `Allow user to add car manually without VIN`,
              },
            },
          },
          vehicleSelection: {
            type: 'object',
            title: 'Vehicle Selection',
            properties: {
              monthlyPayment: {
                type: 'boolean',
                title: 'Show monthly payments',
              },
              daysUntilAgedUnit: {
                type: 'object',
                title: 'Days until Aged Unit',
                properties: {
                  [Condition.NEW]: {
                    type: 'number',
                    title: 'New Cars',
                    minimum: 1,
                    maximum: 1000,
                  },
                  [Condition.USED]: {
                    type: 'number',
                    title: 'Used Cars',
                    minimum: 1,
                    maximum: 1000,
                  },
                },
              },
            },
          },
          [TabletForms.FIVE_LINE]: {
            type: 'object',
            title: '',
            properties: {
              enabled: {
                type: 'boolean',
                title: 'Enable Five Line Credit Application',
                default: true,
              },
            },
          },
          driverLicense: {
            type: 'object',
            title: 'Driver License',
            description: '',
            properties: {
              coloredTrueSize: {
                title: 'Colored and true size (100%)',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              useManualCrop: {
                title: 'Enable Manual Crop Tool',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
          },
          enableSalePendingPrompt: {
            type: 'boolean',
            title: 'Enable Sale Pending Prompt',
          },
        },
      },
      desking: {
        title: 'Desking',
        type: 'object',
        properties: {
          enabled: {
            title: 'Enabled',
            type: 'boolean',
            enum: [true, false],
            default: false,
          },
        },
        dependencies: {
          enabled: {
            oneOf: [
              {
                properties: {
                  enabled: {
                    enum: [false],
                  },
                },
              },
              {
                properties: {
                  enabled: {
                    enum: [true],
                  },
                  defaultPencil: {
                    type: 'boolean',
                    title:
                      'Enable default pencils (Recommended for salesperson desking)',
                    default: false,
                    enum: [true, false],
                  },
                  showLeaseResidualValue: {
                    type: 'boolean',
                    title: 'Show lease residual value on sticker',
                    default: false,
                  },
                  managerDesking: {
                    type: 'boolean',
                    title: 'Enable manager desking',
                    default: true,
                    enum: [true, false],
                  },
                  salespersonDesking: {
                    type: 'boolean',
                    title: 'Enable salesperson desking',
                    default: false,
                    enum: [true, false],
                  },
                  onlineDesking: {
                    type: 'boolean',
                    title: 'Enable desking of online deals',
                    default: false,
                    enum: [true, false],
                  },
                  show10DollarMonthlyPriceRangeByDefault: {
                    type: 'boolean',
                    title:
                      'Show a price range for estimated monthly payments on instore and the pencil editor by default',
                    default: false,
                  },
                  showPriceRangeWithoutAPROnSticker: {
                    type: 'boolean',
                    title: 'Show price range without APR Rate on sticker',
                    default: false,
                  },
                  enable10DollarMonthlyPriceRangeToggle: {
                    type: 'boolean',
                    title:
                      'Enable a toggle in desking for a set price range for estimated monthly payments on instore and the pencil editor',
                    default: false,
                  },
                  monthlyPriceRangeValue: {
                    type: 'number',
                    title:
                      'Price range value for estimated monthly payments on instore and the pencil editor',
                    default: 10,
                  },
                  useTradeInAllowanceForTax: {
                    type: 'object',
                    title: '',
                    properties: {
                      enabled: {
                        title:
                          'Use Trade Allowance for tax calculation by default',
                        type: 'boolean',
                        enum: [true, false],
                        default: true,
                      },
                    },
                    dependencies: {
                      enabled: {
                        oneOf: [
                          {
                            properties: {
                              enabled: {
                                enum: [false],
                              },
                            },
                          },
                          {
                            properties: {
                              enabled: {
                                enum: [true],
                              },
                              [Condition.USED]: {
                                title: 'Used cars',
                                type: 'boolean',
                                enum: [true, false],
                                default: false,
                              },
                              [Condition.NEW]: {
                                title: 'New cars',
                                type: 'boolean',
                                enum: [true, false],
                                default: true,
                              },
                              ...(formData?.featureConfig?.useCPO?.enabled
                                ? {
                                    [Condition.CPO]: {
                                      title: 'CPO cars',
                                      type: 'boolean',
                                      enum: [true, false],
                                      default: true,
                                    },
                                  }
                                : {}),
                            },
                          },
                        ],
                      },
                    },
                  },
                  showBiweeklyPayments: {
                    type: 'boolean',
                    title: 'Show biweekly payments instead of monthly payments',
                    default: false,
                  },
                  hideNetTradeIn: {
                    type: 'boolean',
                    title: 'Show Trade-In value without the amount owed',
                    default: false,
                  },
                  showAdditionalCustomerIncentives: {
                    type: 'boolean',
                    title: 'Show additional incentives to customer',
                    default: false,
                  },
                  showAllAdditionalCustomerIncentives: {
                    type: 'boolean',
                    title:
                      'Support "Show more" to show all possible incentives to customer',
                    default: false,
                  },
                  fallbackToStandardLeaseRates: {
                    type: 'boolean',
                    title:
                      'Fallback to standard lease rates when special rates are not available',
                    default: false,
                  },
                  filterStandardWhenSpecialExists: {
                    type: 'boolean',
                    title:
                      'Prioritize special offers when both special and standard offers are available; use standard offers if no special offers exist.',
                    default: true,
                  },
                  showSeparateLowInterestHighRebateOffers: {
                    type: 'object',
                    title: '',
                    properties: {
                      enabled: {
                        title:
                          'Show separate Low Interest and High Rebate offers when available (Note: ‘Use Manufacturer APR’ and ‘Use Manufacturer Rebates’ should be ON.)',
                        type: 'boolean',
                        enum: [true, false],
                        default: true,
                      },
                    },
                    dependencies: {
                      enabled: {
                        oneOf: [
                          {
                            properties: {
                              enabled: {
                                enum: [false],
                              },
                            },
                          },
                          {
                            properties: {
                              enabled: {
                                enum: [true],
                              },
                              defaultOffer: {
                                title: 'Default Offer',
                                type: 'string',
                                enum: [
                                  OfferCategoryType.LOW_INTEREST,
                                  OfferCategoryType.HIGH_REBATE,
                                ],
                                enumNames: [
                                  'Lowest interest',
                                  'Highest rebate',
                                ],
                                default: OfferCategoryType.LOW_INTEREST,
                              },
                            },
                          },
                        ],
                      },
                    },
                  },
                  defaultPack: {
                    type: 'number',
                    title: 'Default Pack Value',
                    default: 0,
                  },
                  addInceptionFeesToTotalAtStart: {
                    type: 'boolean',
                    title: 'Add Inception Fees to Total At Start',
                    default: false,
                  },
                  inceptionFeesTotalAtStart: {
                    type: 'object',
                    title: '',
                    properties: {
                      taxes: {
                        type: 'object',
                        title: '',
                        properties: {
                          enabled: {
                            title: 'Include all taxes',
                            type: 'boolean',
                            enum: [true, false],
                            default: true,
                          },
                        },
                      },
                      fees: {
                        type: 'object',
                        title: '',
                        properties: {
                          enabled: {
                            title: 'Include fees',
                            type: 'boolean',
                            enum: [true, false],
                            default: true,
                          },
                        },
                        dependencies: {
                          enabled: {
                            oneOf: [
                              {
                                properties: {
                                  enabled: { enum: [false] },
                                },
                              },
                              {
                                properties: {
                                  enabled: { enum: [true] },
                                  feeStatus: {
                                    type: 'object',
                                    title: '',
                                    default: {},
                                    properties: [
                                      ...(leaseFees?.[FeeCategory.TAXABLE] ??
                                        []),
                                      ...(leaseFees?.[
                                        FeeCategory.NON_TAXABLE
                                      ] ?? []),
                                    ]
                                      .map((fee) => fee.name)
                                      .filter((feeName) => !!feeName)
                                      .reduce(
                                        (acc, feeName) => ({
                                          ...acc,
                                          [feeName]: {
                                            type: 'boolean',
                                            title: feeName,
                                            enum: [true, false],
                                            default: true,
                                          },
                                        }),
                                        {},
                                      ),
                                  },
                                },
                              },
                            ],
                          },
                        },
                      },
                      firstMonth: {
                        type: 'object',
                        title: '',
                        properties: {
                          enabled: {
                            title: 'Include first months payment',
                            type: 'boolean',
                            enum: [true, false],
                            default: true,
                          },
                        },
                      },
                      enabledByDefault: {
                        title: 'Enabled by default',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                      customerToggle: {
                        type: 'object',
                        title: '',
                        properties: {
                          enabled: {
                            title:
                              'Enable "Apply All Taxes And Fees To Total At Start" Toggle',
                            description:
                              'This Should Only Be Selected If Taxes, Fees or First Month Are Added To Total Due At Start',
                            type: 'boolean',
                            enum: [true, false],
                            default: false,
                          },
                        },
                      },
                    },
                  },
                  includeTaxInCapCost: {
                    type: 'boolean',
                    title: 'Include Tax in Capitalized Cost',
                    default: true,
                  },
                  taxInCapCost: {
                    type: 'object',
                    title: '',
                    properties: {
                      capCostReductionTax: {
                        type: 'object',
                        title: '',
                        properties: {
                          enabled: {
                            title:
                              'Include Cap Cost Reduction Tax In Cap cost ',
                            type: 'boolean',
                            enum: [true, false],
                            default: true,
                          },
                        },
                      },
                      paymentStreamTax: {
                        type: 'object',
                        title: '',
                        properties: {
                          enabled: {
                            title: 'Include Payment Stream Tax In Cap Cost',
                            type: 'boolean',
                            enum: [true, false],
                            default: true,
                          },
                        },
                      },
                    },
                  },
                  ignoreMRM: {
                    type: 'boolean',
                    title: 'Ignore MRM completely in leasing calculation',
                    default: false,
                  },
                  applyRebatesToDueAtSigning: {
                    type: 'boolean',
                    title:
                      'Apply Rebates to Due at Signing and Capitalized Cost',
                    default: false,
                  },
                  enableOnePayLease: {
                    type: 'boolean',
                    title: 'Enable One Pay Lease',
                    default: false,
                  },
                  enableLeaseApr: {
                    type: 'boolean',
                    title: 'Enable Lease Apr',
                    default: false,
                  },
                  useDeskingCRMPush: {
                    type: 'boolean',
                    title: 'Enable Desking CRM Push',
                    default: false,
                  },
                },
                dependencies: {
                  salespersonDesking: {
                    oneOf: [
                      {
                        properties: {
                          salespersonDesking: {
                            enum: [false],
                          },
                        },
                      },
                      {
                        properties: {
                          salespersonDesking: {
                            enum: [true],
                          },
                          salespersonDeskingPushToCRM: {
                            type: 'boolean',
                            title: 'Enable push to CRM for salesperson desking',
                            default: false,
                          },
                        },
                      },
                    ],
                  },
                },
              },
            ],
          },
        },
      },
      wizard: {
        title: 'Upstart Online',
        type: 'object',
        properties: {
          leadForm: {
            title: 'Lead Form Config',
            type: 'object',
            properties: {
              piiFlow: {
                title: 'PII Workflow',
                description: 'Require PII at First or Second Step of Workflow',
                type: 'number',
                enum: [1, 2, -1],
                enumNames: ['First Step', 'Second Step', 'PII in Flow'],
                uniqueItems: true,
                default: 1,
              },
              zipCode: {
                title: '',
                type: 'object',
                properties: {
                  enabled: {
                    title: 'Enable Zip Code on PII form',
                    type: 'boolean',
                    default: false,
                    enum: [true, false],
                  },
                },
                dependencies: {
                  enabled: {
                    oneOf: [
                      {
                        properties: {
                          enabled: {
                            enum: [false],
                          },
                        },
                      },
                      {
                        properties: {
                          enabled: {
                            enum: [true],
                          },
                          mandatory: {
                            type: 'boolean',
                            title: 'Make Zip Code mandatory',
                          },
                        },
                      },
                    ],
                  },
                },
              },
              preferredContact: {
                title: '',
                type: 'object',
                properties: {
                  enabled: {
                    title: 'Enable Preferred Contact on PII form',
                    type: 'boolean',
                    default: false,
                    enum: [true, false],
                  },
                },
              },
            },
          },
          splashPage: {
            title: 'Splash Page Settings',
            type: 'object',
            properties: {
              alternateHeaders: {
                title: 'Use Alternate Headers',
                description: 'Display Alternate Lead Capture Form Headers',
                type: 'boolean',
                enumNames: [
                  'Yes, display alternate text',
                  'No, default text is fine',
                ],
                default: false,
              },
            },
          },
          hotLeads: {
            title: 'Hot Leads Settings',
            type: 'object',
            properties: {
              enabled: {
                title: 'Enable Hot Leads Email',
                description: "Email sent at 7:00am in the dealer's timezone",
                type: 'boolean',
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['emails'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      emails: {
                        type: 'array',
                        title: 'Emails',
                        minItems: 1,
                        items: {
                          type: 'string',
                        },
                      },
                    },
                  },
                ],
              },
            },
          },
          priceValidation: {
            title: 'Price Validation',
            type: 'object',
            properties: {
              enabled: {
                title: 'Price Validation Tab',
                description:
                  'Show Vehicle price validation from KBB, CarGurus, etc.',
                type: 'boolean',
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['kbbApiKey'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      kbbApiKey: {
                        type: 'string',
                        title: 'KBB API Key',
                      },
                    },
                  },
                ],
              },
            },
          },
          disclaimer: {
            type: 'object',
            title: 'Disclaimer',
            properties: {
              payments: {
                title:
                  'Online Only - Customer facing disclaimer on Payments page',
                type: 'string',
              },
            },
          },
          paymentBuilder: {
            type: 'object',
            title: 'Payment Builder',
            properties: {
              showPaymentSummary: {
                title: 'Show Payment Summary Box',
                type: 'boolean',
                enum: [true, false],
                default: true,
              },
            },
          },
          loadCarsFromPartnerStores: {
            title: 'Load cars from partner stores',
            type: 'boolean',
            enum: [true, false],
            default: false,
          },
          routeDealToWebsiteDealer: {
            title: 'Route Deals To Website Dealer',
            type: 'boolean',
            enum: [true, false],
            default: false,
            description: 'Deals should be routed to Website Dealers CRM.',
          },
          googleAnalytics: {
            type: 'object',
            title: 'Google Analytics',
            properties: {
              enabled: {
                title: 'Enable Google Analytics',
                type: 'boolean',
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['trackingId'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      trackingId: {
                        type: 'string',
                        title: 'Tracking ID',
                      },
                    },
                  },
                ],
              },
            },
          },
          facebookPixel: {
            type: 'object',
            title: 'Facebook Pixel',
            properties: {
              enabled: {
                title: 'Enable Facebook Pixel',
                type: 'boolean',
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['facebookPixelId'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      facebookPixelId: {
                        type: 'string',
                        title: 'Facebook Pixel ID',
                      },
                    },
                  },
                ],
              },
            },
          },
          updateEmails: {
            type: 'object',
            title: 'Online Deal Update Notifications',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      recipientEmail: {
                        title: 'Recipient email address',
                        type: 'string',
                      },
                    },
                    required: ['recipientEmail'],
                  },
                ],
              },
            },
          },
          ctaClassic: {
            title: 'Classic CTA behaviour',
            description: 'Load extra messaging after PII',
            type: 'object',
            properties: {
              enabled: {
                title: 'Enable',
                type: 'boolean',
                default: false,
              },
            },
          },
          enableDocUpload: hasUpstartEnabled
            ? {
                type: 'object',
                title: 'Documents Upload',
                description: 'Always Enable for Upstart Lending',
                properties: {
                  enabled: {
                    title: 'Enable',
                    type: 'boolean',
                    default: true,
                    enum: [true, false],
                  },
                },
              }
            : {
                type: 'object',
                title: 'Documents Upload',
                description: 'Web Document Upload',
                properties: {
                  enabled: {
                    title: 'Enable',
                    type: 'boolean',
                    default: false,
                    enum: [false, true],
                  },
                },
              },
        },
      },
      featureConfig: {
        type: 'object',
        title: 'Feature Configurations',
        properties: {
          inventoryImages: {
            title: 'Primary Image Source',
            description:
              'Choose image source to be displayed first on Vehicle Selection and Vehicle Detail',
            type: 'object',
            properties: {
              primaryImageSource: {
                type: 'string',
                enum: inventoryImageSchemaValues.optionValues,
                enumNames: inventoryImageSchemaValues.optionValues,
                uniqueItems: true,
                default: inventoryImageSchemaValues.defaultImageSource,
              },
            },
          },
          showDealerBrandsFirst: {
            title: 'Inventory Brand Display',
            type: 'boolean',
            enum: [false, true],
            enumNames: ['Default', 'Show Dealer Brand First'],
            uniqueItems: true,
            default: false,
          },
          inventoryDefaultSort: {
            title: 'Inventory Default Sort',
            type: 'string',
            enum: Object.values(InventorySortValues),
            enumNames: Object.values(InventorySortValues).map((value) => {
              switch (value) {
                case InventorySortValues.YEAR_ASC:
                  return 'Year old to new';
                case InventorySortValues.YEAR_DESC:
                  return 'Year new to old';
                case InventorySortValues.PRICE_ASC:
                  return 'Price low to high';
                case InventorySortValues.PRICE_DESC:
                  return 'Price high to low';
                case InventorySortValues.MILEAGE_ASC:
                  return 'Miles low to high';
                case InventorySortValues.MILEAGE_DESC:
                  return 'Miles high to low';
                default:
                  return value;
              }
            }),
            default: InventorySortValues.PRICE_ASC,
          },
          showOnLotInventoryFirst: {
            title: 'Inventory Status Display',
            type: 'boolean',
            enum: [false, true],
            enumNames: ['Default', 'Show on-lot inventory first'],
            uniqueItems: true,
            default: false,
          },
          newLendingTiersMatch: {
            type: 'object',
            title: 'New Lending Tiers Match',
            properties: {
              purchase: {
                title: 'Purchase',
                type: 'boolean',
                enum: [false, true],
                enumNames: ['False', 'True'],
                default: false,
              },
              lease: {
                title: 'Lease',
                type: 'boolean',
                enum: [false, true],
                enumNames: ['False', 'True'],
                default: false,
              },
            },
          },
          inventoryConditionDisplay: {
            title: 'Inventory Condition Display',
            type: 'object',
            properties: {
              displayCheckboxes: {
                title:
                  'Choose whether new and used inventory can be displayed simultaneously',
                description:
                  'Check boxes allow new, used, and CPO vehicles to be shown at the same time. Radio buttons allow only 1 condition to be seen at a time',
                type: 'boolean',
                enum: [true, false],
                enumNames: ['Checkboxes', 'Radio'],
                uniqueItems: true,
                default: true,
              },
            },
          },
          useCPO: {
            type: 'object',
            title: 'Use CPO',
            description: 'Enable CPO',
            properties: {
              enabled: {
                title: 'Enable CPO',
                type: 'boolean',
                enum: [true, false],
                default: true,
              },
            },
          },
          ftpUpload: {
            type: 'object',
            title: 'FTP Upload',
            description: 'Upload all Forms to your FTP Server',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['hostname', 'username', 'password'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      hostname: {
                        title: 'Hostname',
                        type: 'string',
                      },
                      username: {
                        title: 'Username',
                        type: 'string',
                      },
                      password: {
                        title: 'Password',
                        type: 'string',
                      },
                      path: {
                        title: 'Path',
                        type: 'string',
                      },
                    },
                  },
                ],
              },
            },
          },
          softCreditPull: {
            type: 'object',
            title: 'Soft Credit Pull',
            description: 'Enable Soft Credit Pull for your customers',
            properties: {
              online: {
                title: 'Online',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              inStore: {
                title: 'Instore DL Scan',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              inStoreCreditApp: {
                title: 'Instore Credit App',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              digitalFinance: {
                title: 'Digital Finance',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              username: {
                title: 'Username',
                type: 'string',
              },
              password: {
                title: 'Password',
                type: 'string',
              },
            },
          },
          urbanScience: {
            type: 'object',
            title: 'Urban Science',
            description: 'Configure Urban Science Integration',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      dealerCode: {
                        title: 'Dealer Code',
                        type: 'string',
                      },
                    },
                    required: ['dealerCode'],
                  },
                ],
              },
            },
          },
          thirdPartyLeads: {
            type: 'object',
            title: 'Third Party Leads',
            description: 'Enable Third Party Leads Forwarding',
            properties: {
              forwardEmailToCustomer: {
                title: 'Forward Email to Customer',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              forwardTextToCustomer: {
                title: 'Forward Text to Customer',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              emailSenderName: {
                title: 'Display Name of Sender in Email and Text Msg',
                type: ['string', 'null'],
                default: '',
              },
            },
            dependencies: {
              forwardEmailToCustomer: {
                oneOf: [
                  {
                    properties: {
                      forwardEmailToCustomer: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    properties: {
                      forwardEmailToCustomer: {
                        enum: [true],
                      },
                      forwardEmailDelayMinutes: {
                        title: 'Email Delay (minutes)',
                        type: 'number',
                        minimum: 0,
                        maximum: 1440, // one day
                        default: 0,
                      },
                    },
                    required: ['forwardEmailDelayMinutes'],
                  },
                ],
              },
              forwardTextToCustomer: {
                oneOf: [
                  {
                    properties: {
                      forwardTextToCustomer: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    properties: {
                      forwardTextToCustomer: {
                        enum: [true],
                      },
                      forwardTextDelayMinutes: {
                        title: 'Text Delay (minutes)',
                        type: 'number',
                        minimum: 0,
                        maximum: 1440, // one day
                        default: 0,
                      },
                    },
                    required: ['forwardTextDelayMinutes'],
                  },
                ],
              },
            },
          },
          deposits: {
            type: 'object',
            title: 'Deposits',
            description: 'Deposits to reserve a vehicle',
            properties: {
              enabled: {
                title: 'Enable',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      newCars: {
                        title: 'Enable for New Cars',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                      usedCars: {
                        title: 'Enable for Used Cars',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                      ...(formData?.featureConfig?.useCPO?.enabled
                        ? {
                            cpoCars: {
                              title: 'Enable for CPO Cars',
                              type: 'boolean',
                              enum: [true, false],
                              default: false,
                            },
                          }
                        : {}),
                      amount: {
                        title: 'Deposit Amount',
                        type: 'number',
                        minimum: 1,
                        default: 200,
                      },
                    },
                  },
                ],
              },
            },
          },
          crmDirectLogin: {
            type: 'object',
            title: 'CRM Direct Login',
            description: 'Enable CRM Direct Login',
            properties: {
              enabled: {
                title: 'CRM Direct Login',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
          },
          virtualShowroom: {
            type: 'object',
            title: 'Virtual Showroom',
            description: 'Enable Virtual Showroom',
            properties: {
              enabled: {
                title: 'Virtual Showroom',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      displayMultipleChatRequests: {
                        title: 'Display Multiple Chat Requests',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                    },
                  },
                ],
              },
            },
          },
          incentives: {
            type: 'object',
            title: 'Incentives Settings',
            properties: {
              provider: {
                title: 'Incentives Provider',
                description:
                  'Note: Model Code is required in the feed for more accurate incentive calculations',
                type: 'string',
                enum: Object.values(incentivesProviders),
                enumNames: ['AIS'],
                default: incentivesProviders.AIS,
              },
            },
            dependencies: {
              provider: {
                oneOf: [
                  {
                    properties: {
                      provider: {
                        enum: [incentivesProviders.AIS],
                      },
                      excludeIncentivesFromAffiliates: {
                        title: 'Exclude incentives from affiliates',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                    },
                  },
                ],
              },
            },
          },

          downPayment: {
            type: 'object',
            title: 'Downpayment Settings',
            properties: {
              downPaymentOptions: {
                type: 'object',
                title: '',
                properties: {
                  isPercentage: {
                    type: 'boolean',
                    enum: [true, false],
                    enumNames: ['Percentage', 'Dollars'],
                    default: true,
                  },
                },
                dependencies: {
                  isPercentage: {
                    oneOf: [
                      {
                        properties: {
                          isPercentage: {
                            enum: [true],
                          },
                          percentageDownPaymentA: {
                            type: 'number',
                            title: 'Down Payment A',
                            default: 0.1,
                            minimum: 0,
                            maximum: 1,
                          },
                          percentageDownPaymentB: {
                            type: 'number',
                            title: 'Down Payment B',
                            default: 0.2,
                            minimum: 0,
                            maximum: 1,
                          },
                          percentageDownPaymentC: {
                            type: 'number',
                            title: 'Down Payment C',
                            default: 0.3,
                            minimum: 0,
                            maximum: 1,
                          },
                        },
                      },
                      {
                        properties: {
                          isPercentage: {
                            enum: [false],
                          },
                          fixedDownPaymentA: {
                            type: 'number',
                            title: 'Down Payment A',
                            default: 0,
                            minimum: 0,
                          },
                          fixedDownPaymentB: {
                            type: 'number',
                            title: 'Down Payment B',
                            default: 2000,
                            minimum: 0,
                          },
                          fixedDownPaymentC: {
                            type: 'number',
                            title: 'Down Payment C',
                            default: 5000,
                            minimum: 0,
                          },
                        },
                      },
                    ],
                  },
                },
              },
              disableEditBySalesPerson: {
                type: 'boolean',
                title:
                  'Do not allow in-store salesperson to change the down-payment',
                default: false,
              },
            },
          },

          multiDealerManagement: {
            type: 'object',
            title: 'Multiple Dealership Management Settings',
            properties: {
              loadCarsFromPartnerStoresInStore: {
                type: 'boolean',
                title: 'Enable loading cars from partner stores in In-Store',
                default: false,
              },
              // turn off loadCarsFromPartnerStoresInStore and turn this on if we want to display cars
              // not on the lot that are not from Partner stores but only from Feedviews
              displayCarsNotOnLotInStore: {
                type: 'boolean',
                title: 'Display cars not in lot in In-Store',
                default: false,
                description:
                  'Display cars not in lot without enabling loading cars from partner stores in In-Store. Allows us to display cars from Feedviews. ',
              },
              enableCrossSellingFromPartnerStores: {
                type: 'boolean',
                title: 'Enable cross selling vehicles from partner stores',
                default: false,
              },
            },
          },
          kbbDefaultOption: {
            type: 'object',
            title: 'Trade-In with KBB Default Option Online',
            description:
              'The KBB option is enabled by default if no other trade-in types are configured (ie, AccuTrade, ICO, Blackbook, TradePending). You can turn this config "off" to hide the KBB default from showing online.',
            properties: {
              enabled: {
                title: 'Show default KBB option online',
                type: 'boolean',
                enum: [true, false],
                default: true,
              },
            },
          },
          ...(formData?.brand
            ?.map((b) => b.toLowerCase())
            .includes(Makes.Subaru)
            ? {
                subaruGtp: {
                  type: 'object',
                  title: 'Subaru Guaranteed Trade Program',
                  properties: {
                    enabled: {
                      title: 'Enabled',
                      type: 'boolean',
                      enum: [true, false],
                      default: false,
                    },
                  },
                  dependencies: {
                    enabled: {
                      oneOf: [
                        {
                          properties: {
                            enabled: {
                              enum: [false],
                            },
                          },
                        },
                        {
                          properties: {
                            enabled: {
                              enum: [true],
                            },
                            showKBBOption: {
                              title: 'Show KBB Option',
                              type: 'boolean',
                              enum: [true, false],
                              default: false,
                            },
                          },
                        },
                      ],
                    },
                  },
                },
              }
            : {}),

          acuTrade: {
            type: 'object',
            title: 'TradeIns powered by Accu-Trade',
            description:
              'Get firm offers instantly for trade-ins via Accu-Trade',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              enabledInstore: {
                title: 'Enabled In Store',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['dealerID', 'dealerKey'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      dealerID: {
                        title: 'Accu-Trade Dealer ID',
                        type: 'string',
                      },
                      dealerKey: {
                        title: 'API Key',
                        type: 'string',
                      },
                    },
                  },
                ],
              },
              enabledInstore: {
                oneOf: [
                  {
                    properties: {
                      enabledInstore: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['dealerID', 'dealerKey'],
                    properties: {
                      enabledInstore: {
                        enum: [true],
                      },
                      dealerID: {
                        title: 'Accu-Trade Dealer ID',
                        type: 'string',
                      },
                      dealerKey: {
                        title: 'API Key',
                        type: 'string',
                      },
                    },
                  },
                ],
              },
            },
          },

          kbbIco: {
            type: 'object',
            title: 'Trade-Ins with KBB Instant Cash Offer',
            description: 'Get firm offers instantly for trade-ins via KBB ICO',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              enabledInstore: {
                title: 'Enabled In Store',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['dealerId'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      dealerId: {
                        title: 'KBB ICO Dealer ID',
                        type: 'string',
                      },
                      groupIdRequired: {
                        title: 'Has KBB group ID',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                    },
                    dependencies: {
                      groupIdRequired: {
                        oneOf: [
                          {
                            required: ['groupId'],
                            properties: {
                              groupIdRequired: {
                                enum: [true],
                              },
                              groupId: {
                                title: 'Group Id',
                                type: 'string',
                                default: '',
                              },
                            },
                          },
                          {
                            properties: {
                              groupIdRequired: {
                                enum: [false],
                              },
                            },
                          },
                        ],
                      },
                    },
                  },
                ],
              },
              enabledInstore: {
                oneOf: [
                  {
                    properties: {
                      enabledInstore: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['dealerId'],
                    properties: {
                      enabledInstore: {
                        enum: [true],
                      },
                      dealerId: {
                        title: 'KBB ICO Dealer ID',
                        type: 'string',
                      },
                      groupIdRequired: {
                        title: 'Has KBB group ID',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                    },
                    dependencies: {
                      groupIdRequired: {
                        oneOf: [
                          {
                            required: ['groupId'],
                            properties: {
                              groupIdRequired: {
                                enum: [true],
                              },
                              groupId: {
                                title: 'Group Id',
                                type: 'string',
                                default: '',
                              },
                            },
                          },
                          {
                            properties: {
                              groupIdRequired: {
                                enum: [false],
                              },
                            },
                          },
                        ],
                      },
                    },
                  },
                ],
              },
            },
          },

          blackBook: {
            type: 'object',
            title: 'Trade-Ins with Black Book',
            description: 'Get estimates for trade-ins via Black Book',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
          },

          tradePending: {
            type: 'object',
            title: 'Trade-Ins with TradePending',
            description: 'Get estimates for trade-ins via TradePending',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      showInStore: {
                        title: 'Show In Store',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                      showOnline: {
                        title: 'Show Online',
                        type: 'boolean',
                        enum: [true, false],
                        default: false,
                      },
                    },
                  },
                ],
              },
            },
          },

          forwardInstabugReports: {
            type: 'object',
            title: 'Forward Instabug Reports',
            description:
              'Forward issues filed through Instabug to the provided email addresses',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['emails'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      emails: {
                        type: 'array',
                        title: 'Emails',
                        minItems: 1,
                        items: {
                          type: 'string',
                        },
                      },
                    },
                  },
                ],
              },
            },
          },

          formsConfig: {
            type: 'object',
            title: 'Global Credit Application Configuration',
            properties: {
              mandatoryEmployersAddress: {
                title: "Employer's address should be mandatory by default",
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
          },
          displayPricesWithIncentives: {
            title:
              "Display Price includes Manufacturer's Incentives and Rebates",
            type: 'object',
            properties: {
              adjustOnline: {
                title: 'Adjust Online Display Prices with Incentives',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
          },

          downPaymentTradeValueLimit: {
            type: 'object',
            title:
              'Would you like to set a limit on down payment and trade value?',
            description:
              'You can setup a maximum percentage amount of the selected vehicles value to limit the online users from exceeding that value. This includes any trade-ins the customer has submitted.',
            properties: {
              [FinanceType.PURCHASE]: {
                type: 'object',
                title: '',
                properties: {
                  enabled: {
                    title: 'Enabled for Finance',
                    type: 'boolean',
                    enum: [true, false],
                    default: false,
                  },
                },
                dependencies: {
                  enabled: {
                    oneOf: [
                      {
                        properties: {
                          enabled: {
                            enum: [false],
                          },
                        },
                      },
                      {
                        required: ['percentage'],
                        properties: {
                          enabled: {
                            enum: [true],
                          },
                          percentage: {
                            type: 'number',
                            title: 'Percentage value for Finance',
                            minimum: 0,
                            maximum: 1,
                          },
                        },
                      },
                    ],
                  },
                },
              },
              [FinanceType.LEASE]: {
                type: 'object',
                title: '',
                properties: {
                  enabled: {
                    title: 'Enabled for Lease',
                    type: 'boolean',
                    enum: [true, false],
                    default: false,
                  },
                },
                dependencies: {
                  enabled: {
                    oneOf: [
                      {
                        properties: {
                          enabled: {
                            enum: [false],
                          },
                        },
                      },
                      {
                        required: ['percentage'],
                        properties: {
                          enabled: {
                            enum: [true],
                          },
                          percentage: {
                            type: 'number',
                            title: 'Percentage value for Lease',
                            minimum: 0,
                            maximum: 1,
                          },
                        },
                      },
                    ],
                  },
                },
              },
            },
          },
          eContracting: {
            type: 'object',
            title: 'E-Contracting',
            description: 'Enable E-Contracting for your customers',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
          },
          displaySavings: {
            type: 'object',
            title: 'Display savings',
            description: 'Display savings in SRP and VDP',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
          },
          // Days until first lease payment
          daysToFirstPayment: {
            title: 'Number of days until first financing/purchase payment',
            type: 'number',
            default: 30,
            maximum: 365,
            minimum: 1,
          },
          customFeedPrice: {
            type: 'object',
            title: 'Custom Feed Price',
            properties: {
              enabled: {
                type: 'boolean',
                default: false,
                title: 'Enable custom feed price',
              },
            },
          },
          tradeEquity: {
            type: 'object',
            title: 'Enable Trade Equity / Lease Payoff',
            properties: {
              enabled: {
                title: 'Enabled Online and In-Store',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
          },
          showClassicOffers: {
            type: 'object',
            title: 'Classic offers presentation',
            properties: {
              enabled: {
                title: 'Enabled In-Store',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
          },
          ...(isAudiDealer
            ? {
                audiInTransit: {
                  type: 'object',
                  title: 'Audi In Transit',
                  description: 'Configure Audi In Transit Integration.',
                  properties: {
                    enabled: {
                      title: 'Enabled',
                      type: 'boolean',
                      enum: [true, false],
                      default: false,
                    },
                  },
                  dependencies: {
                    enabled: {
                      oneOf: [
                        {
                          properties: {
                            enabled: {
                              enum: [false],
                            },
                          },
                        },
                        {
                          properties: {
                            enabled: {
                              enum: [true],
                            },
                            dealerCode: {
                              title: 'Dealer Code',
                              type: 'string',
                              default: uniteDealerCode,
                            },
                          },
                          required: ['dealerCode'],
                        },
                      ],
                    },
                  },
                },
              }
            : {}),
          sellerRightToCancel: {
            type: 'object',
            title: "Seller's Right to Cancel",
            description:
              'Default right-to-cancel days and fee for RIC deals. Days supported: CO, FL, OH. Fee supported: FL only.',
            properties: {
              days: {
                title: 'Right to Cancel Days',
                type: 'integer',
                minimum: 0,
              },
              fee: {
                title: 'Right to Cancel Fee ($)',
                type: 'number',
                minimum: 0,
                multipleOf: 0.01,
              },
            },
          },
        },
      },
    },
  };
};
